import React, { createContext, useContext } from "react";

import { MeetingStatus } from "../../types";
import { getCalendarMeetingTypes } from "../../components/calendar/calendar-status-utils";

interface CalendarContextValue {
    selectedOrganisationIds: string[]
    showProposed: boolean
    visibleMeetingTypes: MeetingStatus[]
    visiblePatientIds: string[]
    hasInteractedWithPatientFilter: boolean
    visibleWeekDays: number[]
    focusStartHour: number
    focusEndHour: number
    updateSelectedOrganisations: (organisationIds: string[]) => void
    updateShowProposed: (show: boolean) => void
    updateVisibleMeetingTypes: (meetingTypes: MeetingStatus[]) => void
    toggleVisibleMeetingType: (meetingType: MeetingStatus) => void
    updateVisiblePatientIds: (patientIds: string[]) => void
    toggleVisiblePatientId: (patientId: string) => void
    resetVisiblePatientFilter: () => void
    updateVisibleWeekDays: (weekDays: number[]) => void
    toggleVisibleWeekDay: (weekDay: number) => void
    updateFocusHours: (startHour: number, endHour: number) => void
}

interface CalendarContextProps {
    children: React.ReactNode;
}

const CalendarContext = createContext<CalendarContextValue | undefined>(undefined);

const getStringArray = (variableName: string): string[] => {
    const foundValue = localStorage.getItem(variableName)

    if (!foundValue) {
        return []
    }

    return foundValue.split(",")
}

const getShowProposed = (): boolean => {
    const showProposed = localStorage.getItem("showProposed")

    if (showProposed === undefined || showProposed === null) {
        return true
    }

    return showProposed === "true"
}

const getVisibleMeetingTypes = (): MeetingStatus[] => {
    const filterableMeetingTypes = getCalendarMeetingTypes()
    const storedMeetingTypes = localStorage.getItem("visibleMeetingTypes")

    if (!storedMeetingTypes) {
        return getShowProposed()
            ? [...filterableMeetingTypes]
            : filterableMeetingTypes.filter((meetingType) => meetingType !== MeetingStatus.Proposed)
    }

    const visibleMeetingTypes = storedMeetingTypes
        .split(",")
        .filter((meetingType): meetingType is MeetingStatus => {
            return filterableMeetingTypes.includes(meetingType as MeetingStatus)
        })

    if (visibleMeetingTypes.length === 0) {
        return [...filterableMeetingTypes]
    }

    return visibleMeetingTypes
}

const defaultVisibleWeekDays = [1, 2, 3, 4, 5]

const getVisibleWeekDays = (): number[] => {
    const storedWeekDays = localStorage.getItem("visibleWeekDays")

    if (!storedWeekDays) {
        return [...defaultVisibleWeekDays]
    }

    return storedWeekDays
        .split(",")
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value) && value >= 0 && value <= 6)
}

const normalizeFocusHours = (startHour: number, endHour: number): { startHour: number, endHour: number } => {
    const boundedStartHour = Math.max(0, Math.min(23, startHour))
    const boundedEndHour = Math.max(1, Math.min(24, endHour))

    if (boundedEndHour <= boundedStartHour) {
        return {
            startHour: boundedStartHour,
            endHour: Math.min(24, boundedStartHour + 1),
        }
    }

    return {
        startHour: boundedStartHour,
        endHour: boundedEndHour,
    }
}

const getFocusStartHour = (): number => {
    const storedValueText = localStorage.getItem("calendarFocusStartHour")
    const storedValue = storedValueText === null || storedValueText === "" ? Number.NaN : Number(storedValueText)

    if (!Number.isFinite(storedValue)) {
        return 6
    }

    return Math.max(0, Math.min(23, storedValue))
}

const getFocusEndHour = (): number => {
    const storedValueText = localStorage.getItem("calendarFocusEndHour")
    const storedValue = storedValueText === null || storedValueText === "" ? Number.NaN : Number(storedValueText)

    if (!Number.isFinite(storedValue)) {
        return 18
    }

    return Math.max(1, Math.min(24, storedValue))
}

export const CalendarContextProvider: React.FC<CalendarContextProps> = ({
    children,
}) => {
    const [selectedOrganisationIds, setSelectedOrganisationIds] = React.useState<string[]>(getStringArray("lastOrganisationId"))
    const [visibleMeetingTypes, setVisibleMeetingTypes] = React.useState<MeetingStatus[]>(getVisibleMeetingTypes())
    const [visiblePatientIds, setVisiblePatientIds] = React.useState<string[]>([])
    const [hasInteractedWithPatientFilter, setHasInteractedWithPatientFilter] = React.useState(false)
    const [visibleWeekDays, setVisibleWeekDays] = React.useState<number[]>(getVisibleWeekDays())
    const [focusHours, setFocusHours] = React.useState(() => normalizeFocusHours(getFocusStartHour(), getFocusEndHour()))

    const showProposed = visibleMeetingTypes.includes(MeetingStatus.Proposed)
    const focusStartHour = focusHours.startHour
    const focusEndHour = focusHours.endHour

    const updateSelectedOrganisations = (organisationId: string[]) => {
        setSelectedOrganisationIds(organisationId)
        localStorage.setItem("lastOrganisationId", organisationId.join(","))
    }

    const updateVisibleMeetingTypes = (meetingTypes: MeetingStatus[]) => {
        setVisibleMeetingTypes(meetingTypes)
        localStorage.setItem("visibleMeetingTypes", meetingTypes.join(","))
        localStorage.setItem("showProposed", meetingTypes.includes(MeetingStatus.Proposed) ? "true" : "false")
    }

    const updateShowProposed = (showProposed: boolean) => {
        const nextMeetingTypes = showProposed
            ? Array.from(new Set([...visibleMeetingTypes, MeetingStatus.Proposed]))
            : visibleMeetingTypes.filter((meetingType) => meetingType !== MeetingStatus.Proposed)

        updateVisibleMeetingTypes(nextMeetingTypes)
    }

    const toggleVisibleMeetingType = (meetingType: MeetingStatus) => {
        const nextMeetingTypes = visibleMeetingTypes.includes(meetingType)
            ? visibleMeetingTypes.filter((item) => item !== meetingType)
            : [...visibleMeetingTypes, meetingType]

        updateVisibleMeetingTypes(nextMeetingTypes)
    }

    const updateVisiblePatientIds = (patientIds: string[]) => {
        const nextPatientIds = patientIds.filter((patientId, index) => patientId.length > 0 && patientIds.indexOf(patientId) === index)

        setHasInteractedWithPatientFilter(true)
        setVisiblePatientIds(nextPatientIds)
    }

    const toggleVisiblePatientId = (patientId: string) => {
        if (!patientId) {
            return
        }

        const nextPatientIds = visiblePatientIds.includes(patientId)
            ? visiblePatientIds.filter((item) => item !== patientId)
            : [...visiblePatientIds, patientId]

        updateVisiblePatientIds(nextPatientIds)
    }

    const resetVisiblePatientFilter = () => {
        setHasInteractedWithPatientFilter(false)
        setVisiblePatientIds([])
    }

    const updateVisibleWeekDays = (weekDays: number[]) => {
        setVisibleWeekDays(weekDays)
        localStorage.setItem("visibleWeekDays", weekDays.join(","))
    }

    const toggleVisibleWeekDay = (weekDay: number) => {
        const nextWeekDays = visibleWeekDays.includes(weekDay)
            ? visibleWeekDays.filter((item) => item !== weekDay)
            : [...visibleWeekDays, weekDay].sort((a, b) => a - b)

        updateVisibleWeekDays(nextWeekDays)
    }

    const updateFocusHours = (startHour: number, endHour: number) => {
        const normalizedHours = normalizeFocusHours(startHour, endHour)
        setFocusHours(normalizedHours)
        localStorage.setItem("calendarFocusStartHour", normalizedHours.startHour.toString())
        localStorage.setItem("calendarFocusEndHour", normalizedHours.endHour.toString())
    }

    return <CalendarContext.Provider value={{
        selectedOrganisationIds,
        showProposed,
        visibleMeetingTypes,
        visiblePatientIds,
        hasInteractedWithPatientFilter,
        visibleWeekDays,
        focusStartHour,
        focusEndHour,
        updateSelectedOrganisations,
        updateShowProposed,
        updateVisibleMeetingTypes,
        toggleVisibleMeetingType,
        updateVisiblePatientIds,
        toggleVisiblePatientId,
        resetVisiblePatientFilter,
        updateVisibleWeekDays,
        toggleVisibleWeekDay,
        updateFocusHours,
    }}>{children}</CalendarContext.Provider>;
};

export const useEventCalendarValues = () => {
    const ctx = useContext(CalendarContext);
    if (!ctx) {
        throw new Error("useProposeMeeting must be used inside Context");
    }
    return ctx;
};